import { useMemo } from "react"
import { TiposAlojamientos } from "src/gql/schema"
import { ResumenHospedajeResult } from "src/shared/hooks/useMotorTarifas/useMotorTarifas.type"
import { add } from "src/shared/helpers/calculator"
import { getCurrencyFormat } from "src/utils/string"
import { PendientesSubtotales } from "../Pago.type"
import { capitalize } from "src/shared/helpers/capitalize"
import { useFormatDate } from "src/shared/hooks/useFormatDate"

type Result = {
    resumen_tarifas: {
        hospedaje: ResumenHospedajeResult[]
        personas_extra: ResumenHospedajeResult[]
        horas_extra: ResumenHospedajeResult[]
    }
    subtotales: PendientesSubtotales
}

type GroupMap = Record<string, { nombre: string; items: { label: string; value: string }[]; subtotal: number; dias: number }>

const EMPTY_SUBTOTALES: PendientesSubtotales = {
    subtotal_hospedaje: 0,
    subtotal_horas_extra: 0,
    subtotal_hospedajes_extra: 0,
    subtotal_personas_extra: 0,
    dias_estancia: 0,
    cantidad_horas_extra: 0,
    cantidad_hospedajes_extra: 0,
    cantidad_personas_extra: 0,
    total: 0,
}

const EMPTY: Result = {
    resumen_tarifas: { hospedaje: [], personas_extra: [], horas_extra: [] },
    subtotales: EMPTY_SUBTOTALES,
}

const isPersona = (d: string) => d.toLowerCase().includes("persona")
const isHora = (d: string) => d.toLowerCase().includes("hora")
const isHospedajeExtra = (d: string) => d.toLowerCase().includes("hospedaje")

// Construye el resumen de hospedaje agrupando tarifas y extras relacionados
function buildHospedajeFromRentasTarifas(
    rentasTarifas: any[],
    tipo: TiposAlojamientos,
    extrasCheckin: any[] = [],
    formatFecha: (fecha: string) => string = () => ""
): { resumen: ResumenHospedajeResult[]; subtotal: number; dias: number } {
    const groups: GroupMap = {}
    let subtotal = 0
    let dias = 0

    for (const rt of rentasTarifas) {
        const tarifa_id = rt.tarifa_id || "default"
        const tarifa = rt.tarifa || {}
        const grupo = tarifa.grupo_tarifa || {}
        const nombre = grupo.nombre || tarifa.nombre || ""
        const { precio_unitario = 0, total = 0 } = rt.subtotales || {}
        const noches = rt.subtotales?.noches ?? 1

        if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, items: [], subtotal: 0, dias: 0 }

        const label =
            tipo === TiposAlojamientos.Motel
                ? `${tarifa.duracion_renta || 0} horas - ${getCurrencyFormat(precio_unitario)}`
                : `${
                    noches === 1
                        ? formatFecha(rt.fecha_inicio)
                        : `${formatFecha(rt.fecha_inicio)} - ${formatFecha(rt.fecha_fin)}`
                } | ${getCurrencyFormat(precio_unitario)}`

        groups[tarifa_id].items.push({ label, value: getCurrencyFormat(total) })
        groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, total)

        groups[tarifa_id].dias = add(groups[tarifa_id].dias, noches)
        dias = add(dias, noches)

        subtotal = add(subtotal, total)
    }

    const extrasRelevantes = extrasCheckin.filter(
        (e) => isHora(e.detalle || "") || isHospedajeExtra(e.detalle || "")
    )
    for (const extra of extrasRelevantes) {
        const isHoraExtra = isHora(extra.detalle || "")
        const extrasTarifas: any[] = extra.extras_tarifas || []

        for (const et of extrasTarifas) {
            const tarifa_id = et.tarifa_id || "default"
            const nombre = et.tarifa?.grupo_tarifa?.nombre || et.tarifa?.nombre || ""
            const { precio_unitario = 0, total = 0 } = et.subtotales || {}
            const numeroFromFechas = Math.round(
                (new Date(et.fecha_fin).getTime() - new Date(et.fecha_inicio).getTime()) / (1000 * 60 * 60 * 24)
            )
            const numero = et.subtotales?.numero ?? numeroFromFechas

            if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, items: [], subtotal: 0, dias: 0 }

            const label = isHoraExtra
                ? `${numero} horas | ${getCurrencyFormat(precio_unitario)}`
                : numero === 1
                    ? `${formatFecha(et.fecha_inicio)} | ${getCurrencyFormat(precio_unitario)}`
                    : `${formatFecha(et.fecha_inicio)} - ${formatFecha(et.fecha_fin)} | ${getCurrencyFormat(precio_unitario)}`

            groups[tarifa_id].items.push({ label, value: getCurrencyFormat(total) })
            groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, total)
            if (!isHoraExtra) {
                groups[tarifa_id].dias = add(groups[tarifa_id].dias, numero)
            }
        }

        if (extrasTarifas.length === 0) {
            if (!groups["default"]) groups["default"] = { nombre: extra.detalle || "", items: [], subtotal: 0, dias: 0 }
            const label = isHoraExtra
                ? `${extra.cantidad || 0} horas | ${getCurrencyFormat(extra.precio || 0)}`
                : `${extra.cantidad || 0} noches | ${getCurrencyFormat(extra.precio || 0)}`
            groups["default"].items.push({ label, value: getCurrencyFormat(extra.total || 0) })
            groups["default"].subtotal = add(groups["default"].subtotal, extra.total || 0)
            if (!isHoraExtra) groups["default"].dias = add(groups["default"].dias, extra.cantidad || 0)
        }
    }

    return {
        resumen: Object.values(groups).map((g) => ({ nombre: g.nombre, dias: Math.max(1, g.dias), items: g.items, total: g.subtotal })),
        subtotal,
        dias,
    }
}

// Construye el resumen de horas extra desde conceptos, agrupado por tarifa.
// Entradas con la misma tarifa (o mismo precio cuando tarifa es null) se suman en un solo row.
function buildHoraExtraResumen(conceptos: any[]): ResumenHospedajeResult[] {
    const filtered = conceptos.filter((c) => isHora(c.detalle || ""))
    if (filtered.length === 0) return []

    // itemTotals: label → total acumulado (número), para poder sumar por tarifa+precio
    const groups: Record<string, { nombre: string; itemTotals: Record<string, number>; subtotal: number }> = {}

    for (const c of filtered) {
        const extrasTarifas: any[] = c.extras_tarifas || []
        const hasPerEntryData = extrasTarifas.some((et) => et.tarifa !== null)

        if (extrasTarifas.length > 0 && hasPerEntryData) {
            for (const et of extrasTarifas) {
                const tarifa_id = et.tarifa?.tarifa_id || "default"
                const modalidad = et.tarifa?.grupo_tarifa?.modalidad || ""
                const grupNombre = et.tarifa?.grupo_tarifa?.nombre || et.tarifa?.nombre || ""
                const nombre = modalidad.toLowerCase() === "base"
                    ? capitalize(modalidad)
                    : modalidad ? `${capitalize(modalidad)} - ${grupNombre}` : grupNombre
                const entryTotal = et.subtotales?.total ?? 0
                const precio = et.subtotales?.precio_unitario || c.precio || 0

                if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, itemTotals: {}, subtotal: 0 }

                const label = `1 hora extra - ${getCurrencyFormat(precio)}`
                groups[tarifa_id].itemTotals[label] = add(groups[tarifa_id].itemTotals[label] ?? 0, entryTotal)
                groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, entryTotal)
            }
        } else if (extrasTarifas.length > 0) {
            // tarifa null pero hay entradas: agrupar por precio_unitario y sumar
            for (const et of extrasTarifas) {
                const precio = et.subtotales?.precio_unitario || c.precio || 0
                const groupKey = `price_${precio}`
                const entryTotal = et.subtotales?.total ?? 0

                if (!groups[groupKey]) groups[groupKey] = { nombre: "", itemTotals: {}, subtotal: 0 }

                const label = `1 hora extra - ${getCurrencyFormat(precio)}`
                groups[groupKey].itemTotals[label] = add(groups[groupKey].itemTotals[label] ?? 0, entryTotal)
                groups[groupKey].subtotal = add(groups[groupKey].subtotal, entryTotal)
            }
        } else {
            const precio = c.precio || Math.round((c.total || 0) / Math.max(c.cantidad || 1, 1))
            const groupKey = `price_${precio}`

            if (!groups[groupKey]) groups[groupKey] = { nombre: "", itemTotals: {}, subtotal: 0 }

            const label = `1 hora extra - ${getCurrencyFormat(precio)}`
            groups[groupKey].itemTotals[label] = add(groups[groupKey].itemTotals[label] ?? 0, (c.total || 0))
            groups[groupKey].subtotal = add(groups[groupKey].subtotal, (c.total || 0))
        }
    }

    return Object.values(groups).map((g) => ({
        nombre: g.nombre,
        dias: 0,
        items: Object.entries(g.itemTotals).map(([label, total]) => ({
            label,
            value: getCurrencyFormat(total),
        })),
        total: g.subtotal,
    }))
}

// Construye el resumen de hospedaje extra desde conceptos, agrupado por tarifa y rango de fechas
function buildExtrasConceptosResumen(
    conceptos: any[],
    filterFn: (d: string) => boolean,
    tipo: TiposAlojamientos = TiposAlojamientos.Hotel,
    formatFecha: (fecha: string) => string = () => ""
): ResumenHospedajeResult[] {
    const filtered = conceptos.filter((c) => filterFn(c.detalle || ""))
    if (filtered.length === 0) return []

    const groups: GroupMap = {}

    for (const c of filtered) {
        const extrasTarifas: any[] = c.extras_tarifas || []
        const hasPerEntryData = extrasTarifas.some((et) => et.tarifa !== null)

        if (extrasTarifas.length > 0 && hasPerEntryData) {
            for (const et of extrasTarifas) {
                const tarifa_id = et.tarifa?.tarifa_id || "default"
                const modalidad = et.tarifa?.grupo_tarifa?.modalidad || ""
                const grupNombre = et.tarifa?.grupo_tarifa?.nombre || et.tarifa?.nombre || ""
                const nombre = modalidad.toLowerCase() === "base"
                    ? capitalize(modalidad)
                    : modalidad ? `${capitalize(modalidad)} - ${grupNombre}` : grupNombre
                const entryTotal = et.subtotales?.total ?? 0
                if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, items: [], subtotal: 0, dias: 0 }

                const diffDays = (new Date(et.fecha_fin).getTime() - new Date(et.fecha_inicio).getTime()) / (1000 * 60 * 60 * 24)
                const fechaLabel = diffDays >= 1
                    ? `${formatFecha(et.fecha_inicio)} - ${formatFecha(et.fecha_fin)}`
                    : formatFecha(et.fecha_inicio)
                const label = `${fechaLabel} | ${getCurrencyFormat(et.subtotales?.precio_unitario ?? c.precio ?? 0)}`

                groups[tarifa_id].items.push({ label, value: getCurrencyFormat(entryTotal) })
                groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, entryTotal)
                groups[tarifa_id].dias = add(groups[tarifa_id].dias, et.subtotales?.numero || 0)
            }
        } else if (extrasTarifas.length > 0) {
            // tarifa null pero hay entradas: un row por entrada con rango de fechas
            for (const et of extrasTarifas) {
                if (!groups["default"]) groups["default"] = { nombre: c.detalle || "", items: [], subtotal: 0, dias: 0 }
                const entryTotal = et.subtotales?.total || Math.round((c.total || 0) / extrasTarifas.length)
                const precioUnitario = et.subtotales?.precio_unitario || c.precio || Math.round(entryTotal / Math.max(et.subtotales?.numero || 1, 1))
                const diffDays = (new Date(et.fecha_fin).getTime() - new Date(et.fecha_inicio).getTime()) / (1000 * 60 * 60 * 24)
                const fechaLabel = diffDays >= 1
                    ? `${formatFecha(et.fecha_inicio)} - ${formatFecha(et.fecha_fin)}`
                    : formatFecha(et.fecha_inicio)
                const label = `${fechaLabel} | ${getCurrencyFormat(precioUnitario)}`
                groups["default"].items.push({ label, value: getCurrencyFormat(entryTotal) })
                groups["default"].subtotal = add(groups["default"].subtotal, entryTotal)
                groups["default"].dias = add(groups["default"].dias, et.subtotales?.numero || 0)
            }
        } else {
            // sin extras_tarifas: fallback a nivel concepto
            if (!groups["default"]) groups["default"] = { nombre: c.detalle || "", items: [], subtotal: 0, dias: 0 }
            const precioUnitario = c.precio || Math.round((c.total || 0) / (c.cantidad || 1))
            const label = `${c.cantidad || 0} noches | ${getCurrencyFormat(precioUnitario)}`
            groups["default"].items.push({ label, value: getCurrencyFormat(c.total || 0) })
            groups["default"].subtotal = add(groups["default"].subtotal, c.total || 0)
            groups["default"].dias = add(groups["default"].dias, c.cantidad || 0)
        }
    }

    return Object.values(groups).map((g) => ({ nombre: g.nombre, dias: Math.max(1, g.dias), items: g.items, total: g.subtotal }))
}

// Construye el resumen de personas extra desde extras_checkin
function buildPersonasExtraFromExtrasCheckin(
    extrasCheckin: any[]
): { resumen: ResumenHospedajeResult[]; subtotal: number; cantidad: number } {
    const personasExtras = extrasCheckin.filter((e) => isPersona(e.detalle || ""))
    const groups: GroupMap = {}
    let subtotal = 0
    let cantidad = 0

    for (const extra of personasExtras) {
        const extrasTarifas: any[] = extra.extras_tarifas || []
        for (const et of extrasTarifas) {
            const tarifa_id = et.tarifa_id || "default"
            const nombre = et.tarifa?.grupo_tarifa?.nombre || et.tarifa?.nombre || ""
            const { precio_unitario = 0, total = 0 } = et.subtotales || {}

            if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, items: [], subtotal: 0, dias: 0 }

            groups[tarifa_id].items.push({
                label: `${extra.cantidad || 0} x noche | ${getCurrencyFormat(precio_unitario)} x pers.`,
                value: getCurrencyFormat(total),
            })

            groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, total)
            subtotal = add(subtotal, total)
        }
        cantidad = add(cantidad, extra.cantidad || 0)
    }

    return {
        resumen: Object.values(groups).map((g) => ({ nombre: g.nombre, items: g.items, total: g.subtotal, dias: g.dias })),
        subtotal,
        cantidad,
    }
}

// Construye el resumen de personas extra desde conceptos
function buildPersonasExtraFromConceptos(
    conceptos: any[]
): { resumen: ResumenHospedajeResult[]; subtotal: number; cantidad: number } {
    const personasExtras = conceptos.filter((c) => isPersona(c.detalle || ""))
    // itemTotals: label → total acumulado, para sumar entradas con mismo precio dentro de la misma tarifa
    const groups: Record<string, { nombre: string; itemTotals: Record<string, number>; subtotal: number }> = {}
    let subtotal = 0
    let cantidad = 0

    for (const c of personasExtras) {
        const extrasTarifas: any[] = c.extras_tarifas || []
        const hasPerEntryData = extrasTarifas.some((et) => et.tarifa !== null)

        if (extrasTarifas.length > 0 && hasPerEntryData) {
            for (const et of extrasTarifas) {
                const tarifa_id = et.tarifa?.tarifa_id || "default"
                const modalidad = et.tarifa?.grupo_tarifa?.modalidad || ""
                const grupNombre = et.tarifa?.grupo_tarifa?.nombre || et.tarifa?.nombre || ""
                const nombre = modalidad.toLowerCase() === "base"
                    ? capitalize(modalidad)
                    : modalidad ? `${capitalize(modalidad)} - ${grupNombre}` : grupNombre
                const { precio_unitario = 0, total: entryTotal = 0 } = et.subtotales || {}

                if (!groups[tarifa_id]) groups[tarifa_id] = { nombre, itemTotals: {}, subtotal: 0 }

                const label = `${c.cantidad || 0} x noche | ${getCurrencyFormat(precio_unitario || c.precio || 0)} x pers.`
                groups[tarifa_id].itemTotals[label] = add(groups[tarifa_id].itemTotals[label] ?? 0, entryTotal)
                groups[tarifa_id].subtotal = add(groups[tarifa_id].subtotal, entryTotal)
            }
        } else if (extrasTarifas.length > 0) {
            // tarifa null pero hay entradas: agrupar por precio_unitario y sumar
            for (const et of extrasTarifas) {
                const precio = et.subtotales?.precio_unitario || c.precio || 0
                const groupKey = `price_${precio}`
                const entryTotal = et.subtotales?.total ?? 0

                if (!groups[groupKey]) groups[groupKey] = { nombre: "", itemTotals: {}, subtotal: 0 }

                const label = `${c.cantidad || 0} x noche | ${getCurrencyFormat(precio)} x pers.`
                groups[groupKey].itemTotals[label] = add(groups[groupKey].itemTotals[label] ?? 0, entryTotal)
                groups[groupKey].subtotal = add(groups[groupKey].subtotal, entryTotal)
            }
        } else {
            const precioUnitario = Math.round((c.total || 0) / Math.max(c.cantidad || 1, 1))
            const groupKey = `price_${precioUnitario}`

            if (!groups[groupKey]) groups[groupKey] = { nombre: "", itemTotals: {}, subtotal: 0 }

            const label = `${c.cantidad || 0} persona(s) | ${getCurrencyFormat(precioUnitario)}`
            groups[groupKey].itemTotals[label] = add(groups[groupKey].itemTotals[label] ?? 0, (c.total || 0))
            groups[groupKey].subtotal = add(groups[groupKey].subtotal, (c.total || 0))
        }

        subtotal = add(subtotal, c.total || 0)
        cantidad = add(cantidad, c.cantidad || 0)
    }

    return {
        resumen: Object.values(groups).map((g) => ({
            nombre: g.nombre,
            items: Object.entries(g.itemTotals).map(([label, total]) => ({
                label,
                value: getCurrencyFormat(total),
            })),
            total: g.subtotal,
            dias: 0,
        })),
        subtotal,
        cantidad,
    }
}

// Construye un mapa extra_id + fechas → tarifa para rellenar tarifas null en conceptos
function buildTarifaLookup(rentaExtras: any[]): Map<string, any> {
    const lookup = new Map<string, any>()
    for (const extra of rentaExtras) {
        for (const et of (extra.extras_tarifas || [])) {
            if (et.tarifa) {
                const key = `${extra.extra_id}__${et.fecha_inicio}__${et.fecha_fin}`
                lookup.set(key, et.tarifa)
            }
        }
    }
    return lookup
}

// Genera el resumen y subtotales de pagos pendientes según el origen de datos
export function usePagosPendientesResumen(
    selectedItem: any,
    tipo: TiposAlojamientos = TiposAlojamientos.Hotel,
    rentaExtras: any[] = []
): Result {
    const { formatCustomDate } = useFormatDate()

    return useMemo(() => {
        const formatFecha = (fecha: string) => formatCustomDate(fecha, "MMM, DD")

        if (!selectedItem) return EMPTY

        if (selectedItem.rentas_tarifas?.length) {
            const rentasTarifas: any[] = selectedItem.rentas_tarifas
            const extrasCheckin: any[] = selectedItem.extras_checkin || []

            const { resumen: hospedaje, subtotal: subtotal_hospedaje, dias: dias_estancia } =
                buildHospedajeFromRentasTarifas(rentasTarifas, tipo, extrasCheckin, formatFecha)

            const { resumen: personas_extra, subtotal: subtotal_personas_extra, cantidad: cantidad_personas_extra } =
                buildPersonasExtraFromExtrasCheckin(extrasCheckin)

            const horasExtras = extrasCheckin.filter((e) => isHora(e.detalle || ""))
            const hospedajesExtras = extrasCheckin.filter((e) => isHospedajeExtra(e.detalle || ""))

            const subtotal_horas_extra = horasExtras.reduce((acc, e) => add(acc, e.total || 0), 0)
            const subtotal_hospedajes_extra = hospedajesExtras.reduce((acc, e) => add(acc, e.total || 0), 0)
            const cantidad_horas_extra = horasExtras.reduce((acc, e) => add(acc, e.cantidad || 0), 0)
            const cantidad_hospedajes_extra = hospedajesExtras.reduce((acc, e) => add(acc, e.cantidad || 0), 0)

            const total = add(
                add(add(subtotal_hospedaje, subtotal_personas_extra), subtotal_horas_extra),
                subtotal_hospedajes_extra
            )

            return {
                resumen_tarifas: { hospedaje, personas_extra, horas_extra: [] },
                subtotales: {
                    subtotal_hospedaje,
                    subtotal_horas_extra,
                    subtotal_hospedajes_extra,
                    subtotal_personas_extra,
                    dias_estancia,
                    cantidad_horas_extra,
                    cantidad_hospedajes_extra,
                    cantidad_personas_extra,
                    total,
                },
            }
        }

        if (selectedItem.conceptos?.length) {
            const rawConceptos: any[] = selectedItem.conceptos.filter((c: any) => !c.venta_checkin)

            // Enriquecer conceptos con tarifas reales de renta.extras cuando llegan null
            const tarifaLookup = buildTarifaLookup(rentaExtras)
            const conceptos = tarifaLookup.size > 0
                ? rawConceptos.map((c) => ({
                    ...c,
                    extras_tarifas: (c.extras_tarifas || []).map((et: any) => ({
                        ...et,
                        tarifa: et.tarifa ?? tarifaLookup.get(`${c.extra_id}__${et.fecha_inicio}__${et.fecha_fin}`) ?? null,
                    })),
                }))
                : rawConceptos

            const horas_extra = buildHoraExtraResumen(conceptos)
            const hospedajeExtraResumen = buildExtrasConceptosResumen(conceptos, isHospedajeExtra, tipo, formatFecha)

            const { resumen: personas_extra, subtotal: subtotal_personas_extra, cantidad: cantidad_personas_extra } =
                buildPersonasExtraFromConceptos(conceptos)

            const horasExtras = conceptos.filter((c) => isHora(c.detalle || ""))
            const hospedajesExtras = conceptos.filter((c) => isHospedajeExtra(c.detalle || ""))

            const subtotal_horas_extra = horasExtras.reduce((acc, c) => add(acc, c.total || 0), 0)
            const subtotal_hospedajes_extra = hospedajesExtras.reduce((acc, c) => add(acc, c.total || 0), 0)
            const cantidad_horas_extra = horasExtras.reduce((acc, c) => add(acc, c.cantidad || 0), 0)
            const cantidad_hospedajes_extra = hospedajesExtras.reduce((acc, c) => add(acc, c.cantidad || 0), 0)

            const total = add(
                add(subtotal_personas_extra, subtotal_horas_extra),
                subtotal_hospedajes_extra
            )

            return {
                resumen_tarifas: {
                    hospedaje: hospedajeExtraResumen,
                    personas_extra,
                    horas_extra,
                },
                subtotales: {
                    subtotal_hospedaje: 0,
                    subtotal_horas_extra,
                    subtotal_hospedajes_extra,
                    subtotal_personas_extra,
                    dias_estancia: 0,
                    cantidad_horas_extra,
                    cantidad_hospedajes_extra,
                    cantidad_personas_extra,
                    total,
                },
            }
        }

        return EMPTY
    }, [selectedItem, tipo, rentaExtras])
}
